"use client";
import {
  Listbox,
  ListboxButton,
  ListboxOption,
  ListboxOptions,
} from "@headlessui/react";
import { getDataAutomationGroup } from "@/helpers";
import cn from "classnames";
import { useEffect, useRef, useState } from "react";
import { ALPHABET, AlphabetKey, Tabs } from "./Glossary";
import { CaretDownLine, CheckMarkThin } from "@/icons";
import { Button } from "../Button";

export interface GlossaryMobileProps {
  selectedLetter: AlphabetKey;
  setSelectedLetter: (letter: AlphabetKey) => void;
  tabs: Tabs;
}

const SCROLLBAR_PERCENTAGE_HEIGHT = 30;

const GlossaryMobile = ({
  selectedLetter,
  setSelectedLetter,
  tabs,
}: GlossaryMobileProps) => {
  const navRef = useRef<HTMLDivElement | null>(null);
  const [scrollbarTopPos, setScrollbarTopPos] = useState<number>(0);
  const [scrollbarHeight, setScrollbarHeight] = useState<number>(0);
  const [scrollbarContainer, setScrollbarContainer] =
    useState<HTMLUListElement | null>(null);
  const [isSticky, setIsSticky] = useState<boolean>(false);

  useEffect(() => {
    const handleScroll = () => {
      if (navRef.current) {
        const navPosition = navRef.current.getBoundingClientRect();
        const isSticky = navPosition.top <= 0;
        setIsSticky(isSticky);
      }
    };

    handleScroll();

    window.addEventListener("scroll", handleScroll);

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  useEffect(() => {
    if (scrollbarContainer) {
      if (scrollbarContainer.clientHeight === scrollbarContainer.scrollHeight) {
        // If no scrolling is necessary, set the 'scrollbarHeight' to 0.
        setScrollbarHeight(0);
      } else {
        // If listbox options are scrollable, calculate 'scrollbarHeight' as a percentage
        // of the 'scrollbarContainer' height, based on 'SCROLLBAR_PERCENTAGE_HEIGHT'.
        setScrollbarHeight(
          (SCROLLBAR_PERCENTAGE_HEIGHT / 100) * scrollbarContainer.clientHeight,
        );
      }
    }
  }, [scrollbarContainer]);

  const handleScroll = (e: React.UIEvent<HTMLUListElement>) => {
    // Calculate the top position of the scrollbar based on the current scroll position.
    setScrollbarTopPos(
      (e.currentTarget.scrollTop /
        (e.currentTarget.scrollHeight - e.currentTarget.clientHeight)) *
        (e.currentTarget.clientHeight - scrollbarHeight - 32), // 32px - Vertical margins
    );
  };

  return (
    <div className="relative py-12 md:hidden md:py-16">
      <div
        ref={navRef}
        className={cn(
          "sticky top-0 right-0 left-0 z-10 w-full py-4",
          isSticky && "bg-white shadow-[0_10px_20px_rgba(0,0,0,0.10)]",
        )}
      >
        <div className="swdc-container mx-auto">
          <Listbox value={selectedLetter} onChange={setSelectedLetter}>
            <ListboxButton
              className="border-swdc-gray-200 text-subhead-s text-swdc-blue-500 flex min-h-15.5 w-full flex-row items-center justify-between border border-solid bg-[#F4F7F7] px-4 py-3.5 text-left font-medium"
              {...getDataAutomationGroup("dropdown")}
            >
              {({ open }) => (
                <>
                  {selectedLetter}
                  <CaretDownLine
                    className={cn(
                      open
                        ? "ease rotate-180 transition duration-200"
                        : "ease rotate-0 transition duration-200",
                    )}
                  />
                </>
              )}
            </ListboxButton>
            <ListboxOptions
              as="div"
              className="text-subhead-s text-swdc-black-600 absolute right-0 left-0 z-20 mx-6 overflow-y-auto rounded-lg bg-white shadow-xl focus:outline-none"
            >
              <div
                style={{
                  top: `${scrollbarTopPos}px`,
                  height: `${scrollbarHeight}px`,
                }}
                className="bg-swdc-black-600/50 absolute right-1 z-20 my-4 h-10 w-1 rounded"
              />
              <ul
                onScroll={handleScroll}
                ref={setScrollbarContainer}
                className="max-h-[35vh] overflow-x-auto overflow-y-scroll [scrollbar-width:none] md:max-h-68 [&::-webkit-scrollbar]:hidden"
              >
                {ALPHABET.map((letter) => (
                  <ListboxOption
                    as="li"
                    disabled={tabs[letter].length === 0}
                    key={letter}
                    value={letter}
                    className="hover:bg-swdc-gray-50 data-disabled:bg-swdc-cool-gray-100 data-selected:bg-swdc-silver-200 listbox-focus-visible:outline-none listbox-focus-visible:outline-2 listbox-focus-visible:-outline-offset-2 listbox-focus-visible:outline-swdc-blue-400 flex w-full cursor-pointer items-center justify-between px-4 py-2.5 text-left first:rounded-t-lg last:rounded-b-lg data-disabled:cursor-not-allowed data-selected:font-bold"
                  >
                    {({ selected }) => (
                      <>
                        {letter}
                        {selected && <CheckMarkThin className="w-5" />}
                      </>
                    )}
                  </ListboxOption>
                ))}
              </ul>
            </ListboxOptions>
          </Listbox>
        </div>
      </div>
      <div className="swdc-container mx-auto flex flex-col">
        <p className="text-subhead-xl text-swdc-black-600 mb-4 text-left font-black">
          {selectedLetter}
        </p>
        <ul className="flex flex-col gap-8">
          {tabs[selectedLetter].map((entry, index) => (
            <li
              key={index}
              className="border-swdc-silver-300 flex flex-col gap-4 border-b-1 pb-8"
            >
              <p className="text-subhead-s text-swdc-black-600 leading-7 font-bold">
                {entry.headline?.title}
              </p>
              <div
                className="text-body-s text-swdc-gray-400"
                dangerouslySetInnerHTML={{ __html: entry.description || "" }}
              />
              <div className="flex flex-col">
                <span className="text-body-s text-swdc-gray-400 italic">
                  Read Definition:
                </span>
                {entry.headline?.href && (
                  <div className="text-link-m-mobile">
                    <Button
                      variant="tertiary"
                      size="s"
                      icon="none"
                      className="self-start underline hover:decoration-2 active:decoration-2"
                      href={entry.headline.href}
                      dld={entry.headline?.dld}
                      dlt={entry.headline?.dlt}
                    >
                      {entry.headline?.title}
                    </Button>
                  </div>
                )}
              </div>
              <div className="flex flex-col">
                <span className="text-body-s text-swdc-gray-400 italic">
                  What We Offer:
                </span>
                {entry.solution?.href && (
                  <div className="text-link-m-mobile">
                    <Button
                      variant="tertiary"
                      size="s"
                      icon="none"
                      className="self-start underline hover:decoration-2 active:decoration-2"
                      href={entry.solution.href}
                      dld={entry.solution?.dld}
                      dlt={entry.solution?.dlt}
                    >
                      {entry.solution?.title}
                    </Button>
                  </div>
                )}
              </div>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};

export default GlossaryMobile;
